import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { LogIn, Loader2 } from "lucide-react";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import { formatApiError } from "@/lib/api";
import { customerApi } from "@/lib/customerApi";
import { GoogleTradeButton } from "@/components/GoogleTradeButton";
import Seo from "@/components/Seo";

export default function AccountLogin() {
  const { login, customer } = useCustomerAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const requested = params.get('returnTo') || '';
  const safeReturn = (requested.startsWith('/account/checkout?') || requested === '/cart') && !requested.includes('\\') && !/[\r\n]/.test(requested);
  const returnTo = safeReturn ? requested : '/account';
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [needsVerify, setNeedsVerify] = useState(false);

  useEffect(() => {
    if (customer) nav(returnTo);
  }, [customer, nav, returnTo]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setNeedsVerify(false);
    try {
      await login(email, password);
      toast.success("Signed in");
      nav(returnTo);
    } catch (err) {
      const msg = formatApiError(err);
      if (/verify your email/i.test(msg)) setNeedsVerify(true);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const resendVerification = async () => {
    try {
      await customerApi.post("/account/resend-verification", { email });
      toast.success("If that account needs verifying, we've sent a fresh link. Please check your inbox.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <>
      <Seo title="Trade account login — EI Battery Solutions" description="Sign in to your EI Battery trade account to request quotes and track enquiries." />
      <section className="max-w-md mx-auto px-4 py-16 sm:py-24">
        <h1 className="text-3xl font-bold text-ei-navy">Trade account login</h1>
        <p className="mt-2 text-sm text-ei-graphite/75">Sign in to request quotes and track your enquiries.</p>
        <form onSubmit={submit} className="mt-8 space-y-5" data-testid="account-login-form">
          <label className="block">
            <span className="text-xs font-semibold text-ei-navy">Email</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              data-testid="account-login-email"
              className="mt-1 w-full border border-ei-mist rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:border-ei-navy"
            />
          </label>
          <label className="block">
            <span className="text-xs font-semibold text-ei-navy">Password</span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              data-testid="account-login-password"
              className="mt-1 w-full border border-ei-mist rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:border-ei-navy"
            />
          </label>
          <button
            type="submit"
            disabled={loading}
            data-testid="account-login-submit"
            className="w-full font-display text-sm font-bold bg-ei-navy text-ei-white px-6 py-3.5 rounded-sm hover:bg-ei-ink transition-colors inline-flex items-center justify-center gap-2 min-h-[48px] disabled:opacity-60"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogIn className="w-4 h-4" />} Sign in
          </button>
          <Link to="/account/forgot-password" className="block text-sm font-semibold text-ei-navy hover:text-ei-golddark" data-testid="forgot-password-link">
            Forgot password?
          </Link>
        </form>
        {needsVerify && (
          <div className="mt-4 rounded-sm border border-ei-gold/40 bg-ei-gold/10 p-4 text-sm text-ei-graphite" data-testid="verify-notice">
            <p>Your email isn't verified yet. Please click the link in the verification email we sent you.</p>
            <button type="button" onClick={resendVerification} data-testid="resend-verification" className="mt-2 font-semibold text-ei-navy underline hover:text-ei-golddark">
              Resend verification email
            </button>
          </div>
        )}
        <div className="mt-6 flex items-center gap-3" aria-hidden="true">
          <span className="h-px flex-1 bg-ei-mist" />
          <span className="text-xs text-ei-graphite/50">or</span>
          <span className="h-px flex-1 bg-ei-mist" />
        </div>
        <div className="mt-6">
          <GoogleTradeButton label="Sign in with Google" testId="account-login-google" />
        </div>
        <p className="mt-6 text-sm text-ei-graphite/70">
          Don't have an account?{" "}
          <Link to="/#trade-account" className="font-semibold text-ei-navy hover:text-ei-golddark" data-testid="signup-link">
            Create a trade account
          </Link>
        </p>
      </section>
    </>
  );
}
